import { teamRoleResponseSchema } from '@tempo-io/capacity-dto';
import { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';
import type { TeamRole, TeamRoleWithoutCounts } from '@/common/api/types/teamRoles';

const teamRolesResponseSchema = z.array(teamRoleResponseSchema);

/**
 * The read endpoints are validated because callers depend on the aggregate counts being present -
 * the delete-role dialog decides whether to block on `openPositionCount`, so an absent field must
 * fail loudly rather than read as zero.
 *
 * The mutating endpoints deliberately are not. Nothing reads their body beyond the fields being
 * cached, and by the time a response is parsed the write has already committed - a schema mismatch
 * would surface to the user as a failed rename that actually persisted.
 */
const getTeamRoles = async (): Promise<TeamRole[]> => {
  return teamRolesResponseSchema.parse(
    await tempoApi().get('api/v1/capacity/team-roles').json<unknown>(),
  );
};

const updateTeamRole = async (
  id: number,
  fields: { name?: string; isCritical?: boolean; isDefault?: boolean },
): Promise<TeamRoleWithoutCounts> => {
  return await tempoApi()
    .patch(`api/v1/capacity/team-roles/${id}`, { json: fields })
    .json<TeamRoleWithoutCounts>();
};

const searchTeamRoles = async (query: string): Promise<TeamRole[]> => {
  return teamRolesResponseSchema.parse(
    await tempoApi()
      .get('api/v1/capacity/team-roles/search', { searchParams: { query } })
      .json<unknown>(),
  );
};

const bulkCreateTeamRoles = async (
  teamRoles: Array<{ name: string; isCritical?: boolean; isDefault?: boolean }>,
): Promise<TeamRoleWithoutCounts[]> => {
  return await tempoApi()
    .post('api/v1/capacity/team-roles/bulk', {
      json: {
        roles: teamRoles,
      },
    })
    .json<TeamRoleWithoutCounts[]>();
};

const deleteTeamRole = async (id: number): Promise<void> => {
  await tempoApi().delete(`api/v1/capacity/team-roles/${id}`);
};

export const TeamRolesApi = {
  getTeamRoles,
  searchTeamRoles,
  bulkCreateTeamRoles,
  updateTeamRole,
  deleteTeamRole,
} as const;
